import React, { FC, useState, useCallback, memo } from 'react'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { Box } from 'transfix-ui/core'
import { useDeleteShipmentIssueType } from 'apps/marketplace/apiTypes/shipmentIssues'
import { Table, TableHead, TableBody, TableCell, TableRow, Pagination } from 'transfix-ui'
import { useIssuesContext } from '../IssuesContext'
import ControlPanelDeleteModal from './ControlPanelDeleteModal'
import { MODAL_TYPE } from './ControlPanelIssueTypeModal'
import ControlPanelRow from './ControlPanelRow'
import usePagination from './hooks/usePagination'
import { ShipmentIssueTypeCategory } from './utils/filterIssueTypes'

interface IControlPanelTableProps {
  toggleIssueTypeModal: (action: keyof typeof MODAL_TYPE, id?: string | number) => void
  issueTypes: ShipmentIssueTypeCategory[]
}

enum TABLE_COLUMNS {
  name = 'Name',
  categoy = 'Category',
  description = 'Description',
  owner = 'Default Owner',
  priority = 'Default Priority',
  updated = 'Last Updated',
  edit = '',
}

const ControlPanelTable: FC<IControlPanelTableProps> = memo(
  ({ issueTypes, toggleIssueTypeModal }) => {
    const { openToast } = useGlobalToast()

    const { refetchIssueTypes } = useIssuesContext()
    const { mutate: deleteIssueType } = useDeleteShipmentIssueType({})
    const [deleteIdModal, toggleDeleteModal] = useState<string | number | null>(null)
    const isDeleteModalOpen = !!deleteIdModal
    const deleteHandler = useCallback(
      async (action, id) => {
        if (action === 'confirm') {
          try {
            await deleteIssueType(id)
            refetchIssueTypes()
            openToast({
              toastMessage: 'Issue Type Deleted',
              toastType: 'default',
            })
          } catch (e) {
            openToast({
              toastMessage: 'There was an error deleting Issue Type, please try again later',
              toastType: 'alert',
            })
          }
        }

        toggleDeleteModal(null)
      },
      [deleteIssueType, refetchIssueTypes]
    )

    const {
      currentPage,
      highWindowNumber,
      lowWindowNumber,
      numberOfPages,
      setCurrentPage,
      totalIssues,
      visibleIssueTypes,
    } = usePagination(issueTypes)

    return (
      <>
        <Box>
          Showing {lowWindowNumber} - {highWindowNumber} of {totalIssues} issue types
        </Box>
        <Table>
          <TableHead>
            <TableRow tableCellType='th'>
              {Object.entries(TABLE_COLUMNS).map(([key, value]) => (
                <TableCell
                  tableCellType='th'
                  key={`${key}-column-header`}
                  dataTest={`${key}-column-header`}>
                  {value}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody dataTest='control-panel-table'>
            {visibleIssueTypes.map(issueType => (
              <ControlPanelRow
                key={issueType.id}
                issueType={issueType}
                onDeleteClick={toggleDeleteModal}
                onEditClick={toggleIssueTypeModal}
              />
            ))}
          </TableBody>
        </Table>
        <Box display='flex' justifyContent='center' mt={8}>
          <Pagination
            currentPage={currentPage}
            numberOfPages={numberOfPages}
            onPageChange={setCurrentPage}
          />
        </Box>
        {isDeleteModalOpen && (
          <ControlPanelDeleteModal
            id={deleteIdModal}
            open={isDeleteModalOpen}
            onClose={deleteHandler}
          />
        )}
      </>
    )
  }
)

export default ControlPanelTable
